import React from "react";
import { connect } from "react-redux";

import Button from "components/Button";
import { InlineNote } from "./InlineNote";
import {
  StyledForm,
  FormGroup,
  UpgradeText,
  UpgradeButtonLink,
  UpgradeButtonLinkContainer,
} from "./styledComponents";
import Divider from "components/Divider";
import UpgradeCta from "containers/UpgradeCta";

import { Controller, useWatch } from "react-hook-form";

// Simplified field definitions - all sharing fields in a single flat array
const sharingFields = [
  {
    name: "email_enabled",
    label: "Share by email",
    requiredName: "email_required",
    eligibleToToggle: ["lite", "plus", "pro", "super_admin", "enterprise_admin"],
    eligibleToRequire: ["plus", "pro", "super_admin", "enterprise_admin"],
  },
  {
    name: "phone_enabled",
    label: "Share by text message",
    requiredName: "phone_required",
    eligibleToToggle: ["lite", "plus", "pro", "super_admin", "enterprise_admin"],
    eligibleToRequire: ["plus", "pro", "super_admin", "enterprise_admin"],
  },
  {
    name: "first_name_enabled",
    label: "First name",
    requiredName: "first_name_required",
    eligibleToToggle: ["plus", "pro", "super_admin", "enterprise_admin"],
    eligibleToRequire: ["plus", "pro", "super_admin", "enterprise_admin"],
  },
  {
    name: "last_name_enabled",
    label: "Last name",
    requiredName: "last_name_required",
    eligibleToToggle: ["plus", "pro", "super_admin", "enterprise_admin"],
    eligibleToRequire: ["plus", "pro", "super_admin", "enterprise_admin"],
  },
  {
    name: "date_of_birth_enabled",
    label: "Date of birth",
    requiredName: "date_of_birth_required",
    eligibleToToggle: ["pro", "super_admin", "enterprise_admin"],
    eligibleToRequire: ["pro", "super_admin", "enterprise_admin"],
  },
  {
    name: "zip_code_enabled",
    label: "ZIP code",
    requiredName: "zip_code_required",
    eligibleToToggle: ["pro", "super_admin", "enterprise_admin"],
    eligibleToRequire: ["pro", "super_admin", "enterprise_admin"],
  },
];

const getFieldValue = (data, fieldName) => {
  if (!data) return false;
  var parts = fieldName.split("_");
  const valuePart = parts.pop();
  const fieldKey = parts.join("_");
  const value = data.find(
    (x) =>
      // Use regex with global flag to replace ALL occurrences
      x.name.replace(/_/g, "").replace(/ /g, "").toLowerCase() ===
      fieldKey.replace(/_/g, "").replace(/ /g, "").toLowerCase()
  );
  return value ? value[valuePart] : false;
};

// Toggle switch component matching the design
const ToggleSwitch = ({ checked, onChange, disabled }) => {
  return (
    <label className="relative inline-block w-[44px] h-[24px] cursor-pointer">
      <input
        type="checkbox"
        className="sr-only"
        checked={checked}
        onChange={onChange}
        disabled={disabled}
      />
      <div
        className={`absolute inset-0 rounded-full transition-colors duration-200 ${
          checked ? "bg-[#01FFD6]" : "bg-[#E7EBEC]"
        }`}
      >
        <div
          className={`absolute top-[2px] w-[20px] h-[20px] bg-white rounded-full shadow transition-transform duration-200 ${
            checked ? "translate-x-[22px]" : "translate-x-[2px]"
          }`}
        />
      </div>
    </label>
  );
};

// Checkbox component matching the design
const RequiredCheckbox = ({ checked, onChange, disabled }) => {
  return (
    <label className={`relative inline-block w-[24px] h-[24px] ${disabled ? 'opacity-30 cursor-not-allowed' : 'cursor-pointer'}`}>
      <input
        type="checkbox"
        className="sr-only"
        checked={checked}
        onChange={onChange}
        disabled={disabled}
      />
      <div
        className={`w-[24px] h-[24px] rounded flex items-center justify-center transition-colors duration-200 ${
          checked
            ? "bg-[#007AFF]"
            : "bg-white border-2 border-[#E1E7EA]"
        }`}
      >
        {checked && (
          <svg width="14" height="10" viewBox="0 0 14 10" fill="none">
            <path
              d="M1 5L5 9L13 1"
              stroke="white"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        )}
      </div>
    </label>
  );
};

// Rails (Channel::ALLOWED_MIN_GUEST_AGES) rejects anything else with a 422.
// 13 is the floor and the default every channel starts at.
const MIN_GUEST_AGES = [13, 16, 18, 21];
const DEFAULT_MIN_GUEST_AGE = 13;

const MinGuestAgeField = ({ control, disabled, dobEnabled, error, onEdit }) => {
  const hintId = "min-guest-age-hint";
  const errorId = "min-guest-age-error";
  const describedBy = [hintId, error ? errorId : null].filter(Boolean).join(" ");

  return (
    <div className="py-5 border-t border-[#E7EBEC]">
      <label
        htmlFor="min-guest-age"
        className="block text-[16px] text-[#36474F] font-normal mb-1"
      >
        Minimum guest age
      </label>
      <p id={hintId} className="text-[12px] text-[#91ADBA] m-0 mb-2">
        {dobEnabled
          ? "Guests who enter a birthday below this minimum cannot share. Make Date of birth required above to ask every guest."
          : "Turn on Date of birth above to use this. Without it, guests' ages aren't checked."}
      </p>
      <Controller
        control={control}
        name="min_guest_age"
        render={({ field: { value, onChange, onBlur, name, ref } }) => (
          <select
            id="min-guest-age"
            ref={ref}
            name={name}
            className="custom-select"
            value={String(value ?? DEFAULT_MIN_GUEST_AGE)}
            onChange={(e) => {
              onEdit();
              onChange(Number(e.target.value));
            }}
            onBlur={onBlur}
            disabled={disabled}
            aria-invalid={error ? "true" : "false"}
            aria-describedby={describedBy}
          >
            {MIN_GUEST_AGES.map((age) => (
              <option key={age} value={age}>
                {age === DEFAULT_MIN_GUEST_AGE ? `${age} (default)` : String(age)}
              </option>
            ))}
          </select>
        )}
      />
      {error && (
        <p id={errorId} role="alert" className="text-[12px] text-[#C0392B] m-0 mt-2">
          {error.message || "Couldn't save the minimum guest age."}
        </p>
      )}
    </div>
  );
};

const ShareOptionsForm = ({
  onSubmit,
  submitting,
  edit,
  nextTab,
  isPhotographer,
  useMoveboothTheme,
  permissionRole,
  form,
  ...props
}) => {
  const { control, setValue, handleSubmit } = form;
  const shareForm = useWatch({ control, name: "share_types" }) || [
    { required: false, enabled: false, name: "twitter" },
    { required: false, enabled: false, name: "phone" },
    { required: false, enabled: false, name: "name" },
    { required: false, enabled: false, name: "zip code" },
    { required: false, enabled: false, name: "date of birth" },
    { required: false, enabled: false, name: "email" },
    { required: false, enabled: false, name: "gallery_email" },
    { required: false, enabled: false, name: "gallery_phone" },
    { required: false, enabled: false, name: "gallery_twitter" },
    { required: false, enabled: false, name: "gallery_facebook" },
    { required: false, enabled: false, name: "first_name" },
    { required: false, enabled: false, name: "last_name" },
  ];

  // Helper to update the share_types array
  const updateShareTypes = (fieldName, valuePart, newValue) => {
    const result = [...shareForm];
    const parts = fieldName.split("_");
    parts.pop(); // Remove 'enabled' or 'required'
    const fieldKey = parts.join("_");

    let isValueChanged = false;
    result.forEach((x) => {
      // Use regex with global flag to replace ALL occurrences
      const normalizedName = x.name.replace(/_/g, "").replace(/ /g, "").toLowerCase();
      const normalizedKey = fieldKey.replace(/_/g, "").replace(/ /g, "").toLowerCase();
      if (normalizedName === normalizedKey) {
        // If enabling "required", also enable the field
        if (valuePart === "required" && !x[valuePart] && newValue) {
          x.enabled = true;
        }
        // If disabling the field, also disable required
        if (valuePart === "enabled" && x[valuePart] && !newValue) {
          x.required = false;
        }
        x[valuePart] = newValue;
        isValueChanged = true;
      }
    });

    if (!isValueChanged) {
      console.warn(`Share type not found: ${fieldKey}. Update ignored.`);
    }

    setValue("share_types", result);
  };

  // Filter fields based on user's permission role
  const visibleFields = sharingFields.filter((field) =>
    field.eligibleToToggle.includes(permissionRole)
  );

  // Check if user can make fields required
  const canRequire = ["plus", "pro", "super_admin", "enterprise_admin"].includes(permissionRole);

  // Check if at least one sharing method (email or phone) is enabled
  const isEmailEnabled = getFieldValue(shareForm, "email_enabled");
  const isPhoneEnabled = getFieldValue(shareForm, "phone_enabled");
  const hasAtLeastOneSharingMethod = isEmailEnabled || isPhoneEnabled;

  // The age gate reads the guest's date of birth, so it follows that field's
  // plan eligibility. (Rails itself gates only on channel ownership.)
  const dobField = sharingFields.find((f) => f.name === "date_of_birth_enabled");
  const canSetMinGuestAge = dobField.eligibleToToggle.includes(permissionRole);
  const isDobEnabled = Boolean(getFieldValue(shareForm, "date_of_birth_enabled"));

  return (
    <StyledForm onSubmit={handleSubmit(onSubmit)}>

      <FormGroup>
        {/* Table Header */}
        <div className="flex items-center py-4 border-b border-[#E7EBEC]">
          <div className="flex-1 text-[10px] font-bold tracking-[1.5px] text-[#91ADBA] uppercase">
            Field
          </div>
          <div className="w-[140px] text-center text-[10px] font-bold tracking-[1.5px] text-[#91ADBA] uppercase">
            Make Required?
          </div>
        </div>

        {/* Table Rows */}
        {visibleFields.map((field, idx) => {
          const isEnabled = getFieldValue(shareForm, field.name);
          const isRequired = getFieldValue(shareForm, field.requiredName);
          const canMakeRequired = field.eligibleToRequire.includes(permissionRole);

          return (
            <div
              key={idx}
              className="flex items-center py-5 border-b border-[#E7EBEC] last:border-b-0"
            >
              {/* Field toggle and label */}
              <div className="flex-1 flex items-center gap-4">
                <Controller
                  control={control}
                  name={field.name}
                  render={({ field: fieldRest }) => (
                    <ToggleSwitch
                      checked={isEnabled}
                      onChange={(e) => {
                        updateShareTypes(field.name, "enabled", e.target.checked);
                        fieldRest.onChange(e.target.checked);
                      }}
                    />
                  )}
                />
                <span className="text-[16px] text-[#36474F] font-normal">
                  {field.label}
                </span>
              </div>

              {/* Required checkbox */}
              <div className="w-[140px] flex justify-center">
                {canMakeRequired ? (
                  <Controller
                    control={control}
                    name={field.requiredName}
                    render={({ field: fieldRest }) => (
                      <RequiredCheckbox
                        checked={isRequired}
                        disabled={!isEnabled}
                        onChange={(e) => {
                          updateShareTypes(field.requiredName, "required", e.target.checked);
                          fieldRest.onChange(e.target.checked);
                        }}
                      />
                    )}
                  />
                ) : (
                  <span className="text-[12px] text-[#91ADBA]">—</span>
                )}
              </div>
            </div>
          );
        })}

        {/* Only plans that can collect date of birth can use an age gate */}
        {canSetMinGuestAge && (
          <MinGuestAgeField
            control={control}
            dobEnabled={isDobEnabled}
            disabled={submitting || !isDobEnabled}
            error={form.formState?.errors?.min_guest_age}
            onEdit={() => form.clearErrors("min_guest_age")}
          />
        )}

        {/* Warning message when no sharing method is enabled */}
        {!hasAtLeastOneSharingMethod && (
          <div className="py-4 px-4 my-4 bg-[#FFF3CD] border border-[#FFECB5] rounded-md">
            <p className="text-[14px] text-[#856404] m-0">
              At least one of share by email or share by text message must be enabled, otherwise your guests won't be able to upload and share their images.
            </p>
          </div>
        )}

        {/* Upgrade prompt for lite users */}
        {permissionRole === "lite" && (
          <div className="py-6">
            <UpgradeButtonLinkContainer>
              <UpgradeCta as={UpgradeButtonLink}>
                Upgrade to collect more details or make any fields required
              </UpgradeCta>
            </UpgradeButtonLinkContainer>
          </div>
        )}

        {/* Upgrade prompt for plus users (for DOB and ZIP) */}
        {permissionRole === "plus" && (
          <div className="py-6">
            <UpgradeButtonLinkContainer>
              <UpgradeCta as={UpgradeButtonLink}>
                Upgrade to collect date of birth and ZIP code
              </UpgradeCta>
            </UpgradeButtonLinkContainer>
          </div>
        )}
      </FormGroup>

      <Divider marginBottom={48}></Divider>
      <UpgradeText className="left-pos pdd-20">
        Looking for more? Send us a suggestion.
      </UpgradeText>
      <div className="channel-forms__group">
        {edit || isPhotographer || (!isPhotographer && useMoveboothTheme) ? (
          <Button
            type="submit"
            aria-describedby={hasAtLeastOneSharingMethod ? undefined : "share-save-reason"}
            disabled={submitting || !hasAtLeastOneSharingMethod}
          >
            Save
          </Button>
        ) : (
          <Button
            type="button"
            onClick={() => nextTab()}
            aria-describedby={hasAtLeastOneSharingMethod ? undefined : "share-save-reason"}
            disabled={!hasAtLeastOneSharingMethod}
          >
            Save & continue
          </Button>
        )}
        {!hasAtLeastOneSharingMethod && (
          <InlineNote id="share-save-reason">
            Turn on email or text sharing.
          </InlineNote>
        )}
      </div>
    </StyledForm>
  );
};

const mapStateToProps = (state) => ({});

const mapDispatchToProps = (dispatch) => ({});

export default connect(mapStateToProps, mapDispatchToProps)(ShareOptionsForm);
